import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button, Form, Dropdown, Row, Col, InputGroup, Modal } from "react-bootstrap";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useNavigate, Link } from "react-router";
import { useEffect, useState } from "react";
import { Controller, useFormContext, Resolver } from "react-hook-form";
import { getCountryCallingCode } from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";

import VerticalForm from "../../components/forms/VerticalForm";
import SignUpContainerLayout from "./SignUpContainerLayout";
import { COUNTRY_OPTIONS } from "./countryOptions";
import { useSignupMutation } from "../../features/auth/authApi";

interface UserData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  company: string;
  size: string;
  country: string;
  giftType: string[];
  dialCode: string;
}

const COMPANY_SIZE_OPTIONS = [
  { label: "2–10 employees", value: "2-10" },
  { label: "11–50 employees", value: "11-50" },
  { label: "51–200 employees", value: "51-200" },
  { label: "201–500 employees", value: "201-500" },
  { label: "501–1,000 employees", value: "501-1000" },
  { label: "1,001–5,000 employees", value: "1001-5000" },
  { label: "5,001–10,000 employees", value: "5001-10000" },
  { label: "10,001+ employees", value: "10001-plus" },
];

const GIFT_TYPE_OPTIONS = [
  { label: "Curated Gift Boxes/Baskets", value: "curated" },
  { label: "Gift Cards", value: "gift-cards" },
  { label: "Experiences", value: "experiences" },
  { label: "Treats", value: "treats" },
  { label: "Amazon Products", value: "amazon" },
];

export type SignUpVariant = "default" | "guild";

const schema: yup.ObjectSchema<UserData> = yup
  .object({
    email: yup.string().email("Enter a valid email").required("Email is required"),
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
    country: yup.string().required("Country/Region is required"),
    dialCode: yup.string().required("Select a country to get dial code"),
    phone: yup.string().required("Phone number is required"),
    company: yup.string().required("Company name is required"),
    size: yup.string().required("Company size is required"),
    giftType: yup
      .array()
      .of(yup.string().defined()) // ensure string[]
      .min(1, "Select at least one gift type")
      .required("Select at least one gift type"),
  })
  .required();

const schemaResolver: Resolver<UserData> = yupResolver(schema);

const guildSchema: yup.ObjectSchema<UserData> = schema.shape({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required")
    .test(
      "guild-email",
      "Use your Guild Mortgage email address",
      (value) => (value ?? "").toLowerCase().includes("@guildmortgage.net")
    ),
});

const guildSchemaResolver: Resolver<UserData> = yupResolver(guildSchema);

const Req = () => <span className="text-danger ms-1">*</span>;

const FieldError = ({ name }: { name: keyof UserData }) => {
  const { formState: { errors } } = useFormContext<UserData>();
  const err = errors[name];
  return err ? <div className="invalid-feedback d-block">{(err as any).message}</div> : null;
};

const CountrySelect = () => {
  const { control, setValue, formState } = useFormContext<UserData>();
  const hasError = !!formState.errors.country;
  return (
    <Col xs={12} md={6}>
      <Form.Group className="mb-0">
        <Form.Label>Country/Region<Req /></Form.Label>
        <Controller
          name="country"
          control={control}
          render={({ field }) => (
            <Form.Select
              {...field}
              onChange={(e) => {
                field.onChange(e);
                const iso = e.target.value as CountryCode;
                let code = "";
                try { code = "+" + getCountryCallingCode(iso); } catch { code = ""; }
                setValue("dialCode", code, { shouldDirty: true, shouldValidate: true });
                setValue("phone", "", { shouldDirty: true, shouldValidate: true });
              }}
              className={` ${hasError ? "is-invalid" : ""}`}
            >
              {COUNTRY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          )}
        />
        <FieldError name="country" />
      </Form.Group>
    </Col>
  );
};

const PhoneWithDialCode = () => {
  const { control, watch, formState } = useFormContext<UserData>();
  const dialCode = watch("dialCode") || "";
  const disabled = !dialCode;
  const hasError = !!formState.errors.phone || !!formState.errors.dialCode;

  return (
    <Col xs={12} md={6}>
      <Form.Label>Phone Number<Req /></Form.Label>
      <InputGroup className="mb-0">
        <InputGroup.Text className="rounded-0" style={{ minWidth: "2rem" }}>
          {dialCode || "Code"}
        </InputGroup.Text>
        <Controller
          name="phone"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <Form.Control
              type="tel"
              inputMode="tel"
              placeholder="Enter your phone #"
              disabled={disabled}
              className={` ${hasError ? "is-invalid" : ""}`}
              {...field}
            />
          )}
        />
      </InputGroup>
      {disabled ? <div className="form-text">Please select your country first.</div> : <FieldError name="phone" />}
      <FieldError name="dialCode" />
    </Col>
  );
};

const CompanySizeSelect = () => {
  const { control, formState } = useFormContext<UserData>();
  const hasError = !!formState.errors.size;
  return (
    <Col xs={12}>
      <Form.Group className="mb-0">
        <Form.Label>Company Size<Req /></Form.Label>
        <Controller
          name="size"
          control={control}
          render={({ field }) => (
            <Form.Select className={` ${hasError ? "is-invalid" : ""}`} {...field}>
              {COMPANY_SIZE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          )}
        />
        <FieldError name="size" />
      </Form.Group>
    </Col>
  );
};
const GiftTypeCheckboxDropdown = () => {
  const { control, formState } = useFormContext<UserData>();
  const hasError = !!formState.errors.giftType;
  const [show, setShow] = useState(false);

  return (
    <Col xs={12}>
      <Form.Group className="mb-0">
        <Form.Label>Preferred Gift Type<Req /></Form.Label>
        <Controller
          name="giftType"
          control={control}
          defaultValue={[]}
          render={({ field }) => {
            const selected = new Set(field.value || []);
            const toggle = (val: string) => {
              const next = new Set(selected);
              next.has(val) ? next.delete(val) : next.add(val);
              field.onChange(Array.from(next));
            };
            const buttonText =
              selected.size === 0
                ? "Please select"
                : selected.size === 1
                  ? GIFT_TYPE_OPTIONS.find((o) => selected.has(o.value))?.label ?? "1 selected"
                  : `${selected.size} selected`;

            return (
              <>
                <Dropdown
                  show={show}
                  onToggle={(isOpen) => setShow(!!isOpen)}
                  autoClose="outside"
                  className="w-100 gift-dropdown"
                >
                  <Dropdown.Toggle
                    variant={hasError ? "danger" : "outline-secondary"}
                    className="w-100 text-start rounded-0 pe-5"
                    onClick={() => setShow((s) => !s)}
                  >
                    {buttonText}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100 p-2 rounded-0 ">
                    {GIFT_TYPE_OPTIONS.map((o) => (
                      <div key={o.value} className="px-1 py-1">
                        <Form.Check
                          id={`gift-${o.value}`}
                          type="checkbox"
                          label={o.label}
                          checked={selected.has(o.value)}
                          onChange={() => toggle(o.value)}
                        />
                      </div>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                <FieldError name="giftType" />
              </>
            );
          }}
        />
      </Form.Group>
    </Col>
  );
};

const CalendlyModal = ({ show, onHide, formData }: { show: boolean; onHide: () => void; formData: UserData }) => {
  const fullName = `${formData.firstName} ${formData.lastName}`.trim();
  const calendlyUrl = `https://calendly.com/hello-memorique/30min?name=${encodeURIComponent(fullName)}&email=${encodeURIComponent(formData.email)}`;

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" keyboard size="lg">
      <Modal.Header closeButton className="rounded-0">
        <Modal.Title>Book a Time</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <iframe
          src={calendlyUrl}
          width="100%"
          height="600"
          frameBorder="0"
          title="Calendly Booking"
        />
      </Modal.Body>
      <Modal.Footer className="rounded-0">
        <Button variant="secondary" onClick={onHide} className="rounded-0">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const SignUpInner = ({ variant = "default" }: { variant?: SignUpVariant }) => {
  const navigate = useNavigate();
  const [showBooking, setShowBooking] = useState(false);
  const [formData, setFormData] = useState<UserData | null>(null);
  const [signup, { isLoading }] = useSignupMutation();
  const isGuild = variant === "guild";

  const resolverLocal: Resolver<UserData> = isGuild ? guildSchemaResolver : schemaResolver;

  const onSubmit = async (data: UserData) => {
    try {
      console.log('Raw form data received:', data); // Debug log to see what we're getting

      // Get full country name from the country code
      const countryOption = COUNTRY_OPTIONS.find(option => option.value === data.country);
      const fullCountryName = countryOption ? countryOption.label : data.country;

      // Prepare data according to DTO structure
      const signupData = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        country: fullCountryName, // Use full country name instead of code
        company_name: data.company,
        phone_number: `${data.dialCode}${data.phone}`, // Combine dial code with phone number
        company_size: data.size,
        preferred_gift_type: data.giftType.join(','), // Convert array to comma-separated string
      };

      console.log('Form data being sent:', signupData); // Debug log

      // Call the signup API
      await signup(signupData).unwrap();

      // Store form data and show the Calendly modal
      setFormData(data);
      setShowBooking(true);
    } catch (error) {
      console.error('Signup failed:', error);
      // You can add error handling here (show toast, etc.)
    }
  };

  useEffect(() => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken) navigate("/");
  }, []);

  return (
    <SignUpContainerLayout showGuildBranding={isGuild}>

      <VerticalForm<UserData>
        onSubmit={onSubmit}
        resolver={resolverLocal}
        defaultValues={{
          email: "",
          firstName: "",
          lastName: "",
          giftType: [] as string[],
          dialCode: "+1",
          phone: "",
          country: "US",
          size: "2-10",
          company: "",
        }}
      >
        <Row className="gx-3 gy-2">
          <Col xs={12}>
            <Form.Label>Work Email<Req /></Form.Label>
            <Controller
              name="email"
              render={({ field, fieldState }) => (
                <>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    className={` ${fieldState.error ? "is-invalid" : ""}`}
                    {...field}
                  />
                  {fieldState.error && <div className="invalid-feedback d-block">{fieldState.error.message}</div>}
                </>
              )}
            />
          </Col>

          <Col xs={12} md={6}>
            <Form.Label>First Name<Req /></Form.Label>
            <Controller
              name="firstName"
              render={({ field, fieldState }) => (
                <>
                  <Form.Control
                    type="text"
                    placeholder="Enter your first name"
                    className={` ${fieldState.error ? "is-invalid" : ""}`}
                    {...field}
                  />
                  {fieldState.error && <div className="invalid-feedback d-block">{fieldState.error.message}</div>}
                </>
              )}
            />
          </Col>
          <Col xs={12} md={6}>
            <Form.Label>Last Name<Req /></Form.Label>
            <Controller
              name="lastName"
              render={({ field, fieldState }) => (
                <>
                  <Form.Control
                    type="text"
                    placeholder="Enter your last name"
                    className={` ${fieldState.error ? "is-invalid" : ""}`}
                    {...field}
                  />
                  {fieldState.error && <div className="invalid-feedback d-block">{fieldState.error.message}</div>}
                </>
              )}
            />
          </Col>
          <CountrySelect />
          <PhoneWithDialCode />

          <Col xs={12}>
            <Form.Label>Company Name<Req /></Form.Label>
            <Controller
              name="company"
              render={({ field, fieldState }) => (
                <>
                  <Form.Control
                    type="text"
                    placeholder="Enter your company name"
                    className={` ${fieldState.error ? "is-invalid" : ""}`}
                    {...field}
                  />
                  {fieldState.error && <div className="invalid-feedback d-block">{fieldState.error.message}</div>}
                </>
              )}
            />
          </Col>
          <CompanySizeSelect />
          <GiftTypeCheckboxDropdown />
        </Row>
        {!isGuild && (
        <div className="mt-2" style={{ fontSize: "12px" }}>
          YOU'LL BE ABLE TO BOOK A TIME TO MEET WITH A SALES REPRESENTATIVE ON YOUR NEXT STEP.
        </div>
        )}
        {/* 

        <p className="small text-muted mb-3">
          By submitting you are agreeing to receive information about Memorique news,
          events, and offers. To learn more, review our{" "}
          <a href="/privacy" className="link-dark text-decoration-underline">Privacy Policy</a>.
        </p> */}

        <div className="text-center d-grid">
          <Button
            variant="primary"
            type="submit"
            disabled={isLoading}
            className="continue-btn"
          >
            {isLoading ? "Processing..." : isGuild ? "Sign up" : "Next Step: Book A Call"}
          </Button>
        </div>

        <div className="fine-text">
          <h6 className="my-2">
            Have a question? Email us at{" "}
            <a href="/privacy" className="link-blue text-decoration-underline">hello@memorique.com</a>{" "}
            and we will get back ASAP.
          </h6>
        </div>

        <div className="text-center mt-3">
          <Link to='/login' style={{ color: "#1a202c", fontWeight: 500, textDecoration: "none", fontSize: "12px" }}>
            Already have an account? <span style={{ textDecoration: "underline" }}>Sign in</span>
          </Link>
        </div>
      </VerticalForm>

      {/* Calendly Modal */}
      {formData && (
        <CalendlyModal
          show={showBooking}
          onHide={() => setShowBooking(false)}
          formData={formData}
        />
      )}
    </SignUpContainerLayout>
  );
};

const SignUpPage = ({ variant = "default" }: { variant?: SignUpVariant }) => {
  const googleClientId = import.meta.env.VITE_GOOGLE_ID as string;
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <SignUpInner variant={variant} />
    </GoogleOAuthProvider>
  );
};

export default SignUpPage;
